import { z } from 'zod';

import { WorkflowStageType } from '@nucleus-commerce/api-client/admin';

import { DURATION_UNITS, isWholePositive, toMinutes, type DurationUnit } from './duration';

export const STAGE_KEY = /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/;
export const MAX_STAGES = 30;
export const MAX_SUB_STEPS = 20;
export const MAX_PARCELS = 10;
const MIN_RESPONSE_LIMIT = 5;
/** The backend's cap on durations and response limits: 365 days in minutes. */
export const MAX_DURATION_MINUTES = 525_600;

/** The placeholders the backend fills in a stage email; anything else is refused with a 422. */
export const WORKFLOW_EMAIL_PLACEHOLDERS = [
  'customerName',
  'orderName',
  'storeName',
  'stageName',
  'actionUrl',
  'trackingLinks',
  'fileLinks',
  'certificateNumber',
  'message',
] as const;

export const CUSTOMER_STAGE_TYPES: readonly WorkflowStageType[] = [
  'customer_confirmation',
  'customer_review',
];

const durationUnit = z.enum(DURATION_UNITS);
const emailWhen = z.enum(['none', 'start', 'complete']);

const optionalKey = z
  .string()
  .trim()
  .refine((value) => value === '' || STAGE_KEY.test(value), 'workflows:validation.key');

const durationValue = z
  .string()
  .refine(
    (value) => value.trim() === '' || isWholePositive(value),
    'workflows:validation.duration',
  );

const emailFields = {
  emailWhen,
  emailSubject: z.string().trim().max(200, 'workflows:validation.subjectMax'),
  emailBody: z.string().trim().max(10_000, 'workflows:validation.bodyMax'),
};

export const subStepFormSchema = z.object({
  key: optionalKey,
  name: z.string().trim().min(1, 'workflows:validation.stageName').max(120),
  customerName: z.string().trim().max(120),
  hidden: z.boolean(),
  durationValue,
  durationUnit,
  optional: z.boolean(),
  alertUserIds: z.array(z.string()).max(20),
  ...emailFields,
});

export const parcelFormSchema = z.object({
  key: optionalKey,
  label: z.string().trim().min(1, 'workflows:validation.parcelLabel').max(80),
  carriesItems: z.boolean(),
});

export const stageFormSchema = z.object({
  key: optionalKey,
  name: z.string().trim().min(1, 'workflows:validation.stageName').max(120),
  customerName: z.string().trim().max(120),
  hidden: z.boolean(),
  type: z.enum(WorkflowStageType),
  durationValue,
  durationUnit,
  optional: z.boolean(),
  alertUserIds: z.array(z.string()).max(20),
  ...emailFields,
  subSteps: z.array(subStepFormSchema).max(MAX_SUB_STEPS),
  responseLimitValue: z.string(),
  responseLimitUnit: durationUnit,
  qcTemplateId: z.string(),
  parcels: z.array(parcelFormSchema).max(MAX_PARCELS),
  /** customer_review only: the shopper's approval here is the order's design approval. */
  approvesDesign: z.boolean(),
});

function unknownPlaceholders(text: string): string[] {
  const allowed = new Set<string>(WORKFLOW_EMAIL_PLACEHOLDERS);
  return [...text.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g)]
    .map((match) => match[1] ?? '')
    .filter((name) => !allowed.has(name));
}

export const workflowFormSchema = z
  .object({
    name: z.string().trim().min(1, 'workflows:validation.name').max(120),
    stages: z
      .array(stageFormSchema)
      .min(1, 'workflows:validation.stagesMin')
      .max(MAX_STAGES, 'workflows:validation.stagesMax'),
  })
  .superRefine((values, ctx) => {
    const overCap = (value: string, unit: DurationUnit) =>
      isWholePositive(value) && (toMinutes(value, unit) ?? 0) > MAX_DURATION_MINUTES;
    values.stages.forEach((stage, index) => {
      const at = (...path: Array<string | number>) => ['stages', index, ...path];
      if (stage.type === 'qc' && !stage.qcTemplateId) {
        ctx.addIssue({
          code: 'custom',
          path: at('qcTemplateId'),
          message: 'workflows:validation.qcTemplate',
        });
      }
      if (overCap(stage.durationValue, stage.durationUnit)) {
        ctx.addIssue({
          code: 'custom',
          path: at('durationValue'),
          message: 'workflows:validation.durationMax',
        });
      }
      stage.subSteps.forEach((sub, subIndex) => {
        if (overCap(sub.durationValue, sub.durationUnit)) {
          ctx.addIssue({
            code: 'custom',
            path: at('subSteps', subIndex, 'durationValue'),
            message: 'workflows:validation.durationMax',
          });
        }
      });
      if (CUSTOMER_STAGE_TYPES.includes(stage.type) && stage.responseLimitValue.trim() !== '') {
        const minutes = isWholePositive(stage.responseLimitValue)
          ? toMinutes(stage.responseLimitValue, stage.responseLimitUnit)
          : null;
        if (minutes === null || minutes < MIN_RESPONSE_LIMIT) {
          ctx.addIssue({
            code: 'custom',
            path: at('responseLimitValue'),
            message: 'workflows:validation.responseLimit',
          });
        } else if (minutes > MAX_DURATION_MINUTES) {
          ctx.addIssue({
            code: 'custom',
            path: at('responseLimitValue'),
            message: 'workflows:validation.durationMax',
          });
        }
      }
      for (const field of ['emailSubject', 'emailBody'] as const) {
        const unknown = unknownPlaceholders(stage[field]);
        if (unknown.length > 0) {
          ctx.addIssue({
            code: 'custom',
            path: at(field),
            message: 'workflows:validation.placeholders',
          });
        }
      }
    });
  });

export type SubStepFormValues = z.infer<typeof subStepFormSchema>;
export type ParcelFormValues = z.infer<typeof parcelFormSchema>;
export type StageFormValues = z.infer<typeof stageFormSchema>;
export type WorkflowFormInput = z.input<typeof workflowFormSchema>;
export type WorkflowFormValues = z.output<typeof workflowFormSchema>;

export function emptySubStep(): SubStepFormValues {
  return {
    key: '',
    name: '',
    customerName: '',
    hidden: false,
    durationValue: '',
    durationUnit: 'days',
    optional: false,
    alertUserIds: [],
    emailWhen: 'none',
    emailSubject: '',
    emailBody: '',
  };
}

export function emptyStage(type: WorkflowStageType = 'task'): StageFormValues {
  return {
    key: '',
    name: '',
    customerName: '',
    hidden: false,
    type,
    durationValue: '',
    durationUnit: 'days',
    optional: false,
    alertUserIds: [],
    emailWhen: 'none',
    emailSubject: '',
    emailBody: '',
    subSteps: [],
    responseLimitValue: '',
    responseLimitUnit: 'hours',
    qcTemplateId: '',
    parcels: [],
    approvesDesign: true,
  };
}
